import * as XLSX from 'xlsx';
import { SalesStatistics } from '../types';
import { attributionExportRows } from './attributionExport';

/**
 * Export sales data to Excel file with two sheets: Sessions and Shifts
 * Sessions are sorted from most recent to least recent
 */
export const exportSalesDataToExcel = (data: SalesStatistics[], fileName?: string) => {
  if (!data || data.length === 0) {
    alert('No hay datos para exportar');
    return;
  }

  // Sort data by session_id DESC (most recent first)
  const sortedData = [...data].sort((a, b) => {
    const sessionIdA = (a as any).session_id ?? 0;
    const sessionIdB = (b as any).session_id ?? 0;
    return sessionIdB - sessionIdA;
  });

  // Format dates properly
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Format currency values
  const formatCurrency = (amount: number) => {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate duration in hours
  const calculateDuration = (startStr: string, endStr: string | null) => {
    if (!endStr) return 'En curso';
    const start = new Date(startStr);
    const end = new Date(endStr);
    const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    return `${hours.toFixed(1)}h`;
  };

  // ============================================
  // SHEET 1: SESSIONS DATA
  // ============================================
  const sessionsData = sortedData.map((session, index) => {
    const sessionId = (session as any).session_id ?? session.sales_total_id;
    
    return {
      'No.': index + 1,
      'ID Sesión': sessionId,
      'Fecha Inicio': formatDate(session.start_timestamp),
      'Fecha Final': formatDate(session.end_timestamp),
      'Duración': calculateDuration(session.start_timestamp, session.end_timestamp),
      'Día No.': session.day_number,
      'Ventas Brutas': formatCurrency(session.gross_sales),
      'Ventas Netas': formatCurrency(session.net_sales),
      'Total Cobrado': formatCurrency(session.total_charged),
      'Recibos Válidos': session.valid_receipt_count,
      'Recibos Cancelados': session.cancelled_receipt_count,
      'Ticket Promedio': formatCurrency(session.avg_ticket),
      'Total Propinas': formatCurrency(session.total_tip),
      'Propina Promedio por Ticket': formatCurrency(session.avg_tip_by_ticket),
      'Total Descuentos': formatCurrency(session.total_discounts),
      'Total Cupones': formatCurrency(session.total_coupons),
      'Pagos Pendientes': session.pending_payments_count,
      'Total Pendiente': formatCurrency(session.total_pending),
      'Pérdida Bruta': formatCurrency(session.gross_loss),
      'Pérdida Neta': formatCurrency(session.net_loss),
      'Usuarios Count': session.users_count,
      'Usuario': session.user_name || 'N/A',
      'ID Usuario': session.user_id || 'N/A',
      'Promedio Ventas Brutas': formatCurrency(session.avg_gross_sales),
      'Promedio Ventas Netas': formatCurrency(session.avg_net_sales),
      'Promedio Pérdida Neta': formatCurrency(session.avg_net_loss),
      'Promedio Total Propina': formatCurrency(session.avg_total_tip)
    };
  });

  // ============================================
  // SHEET 2: SHIFTS DATA (NORMALIZED)
  // ============================================
  const shiftsData: any[] = [];
  let shiftRowNumber = 1;

  sortedData.forEach((session) => {
    const sessionId = (session as any).session_id ?? session.sales_total_id;
    const sessionWithShifts = session as any;
    
    if (sessionWithShifts.shifts && typeof sessionWithShifts.shifts === 'object') {
      // Sort shifts by shift_id for consistent ordering
      const shiftsArray = Object.values(sessionWithShifts.shifts).sort((a: any, b: any) => {
        return (a.shift_id ?? 0) - (b.shift_id ?? 0);
      });

      shiftsArray.forEach((shift: any) => {
        shiftsData.push({
          'No.': shiftRowNumber++,
          'ID Sesión': sessionId,
          'ID Turno': shift.shift_id,
          'Nombre Turno': shift.name || 'N/A',
          'Estado': shift.active ? 'Activo' : 'Cerrado',
          'Fecha Inicio': formatDate(shift.start_time),
          'Fecha Final': shift.end_time ? formatDate(shift.end_time) : 'En curso',
          'Duración': calculateDuration(shift.start_time, shift.end_time),
          'Usuario Apertura': shift.open_user_name || 'N/A',
          'ID Usuario Apertura': shift.open_user_id || 'N/A',
          'Usuario Cierre': shift.close_user_name || 'N/A',
          'ID Usuario Cierre': shift.close_user_id || 'N/A',
          'Ventas Brutas': formatCurrency(shift.gross_sales ?? 0),
          'Ventas Netas': formatCurrency(shift.net_sales ?? 0),
          'Total Cobrado': formatCurrency(shift.total_charged ?? 0),
          'Recibos Válidos': shift.valid_receipt_count ?? 0,
          'Recibos Cancelados': shift.cancelled_receipt_count ?? 0,
          'Ticket Promedio': formatCurrency(shift.avg_ticket ?? 0),
          'Total Propinas': formatCurrency(shift.total_tip ?? 0),
          'Propina Promedio por Ticket': formatCurrency(shift.avg_tip_by_ticket ?? 0),
          'Propina Promedio por Usuario': formatCurrency(shift.avg_tip_by_user ?? 0),
          'Total Descuentos': formatCurrency(shift.total_discounts ?? 0),
          'Total Cupones': formatCurrency(shift.total_coupons ?? 0),
          'Pagos Pendientes': shift.pending_payments_count ?? 0,
          'Total Pendiente': formatCurrency(shift.total_pending ?? 0),
          'Pérdida Bruta': formatCurrency(shift.gross_loss ?? 0),
          'Pérdida Neta': formatCurrency(shift.net_loss ?? 0),
          'Usuarios Count': shift.users_count ?? 0
        });
      });
    }
  });

  // Create workbook
  const wb = XLSX.utils.book_new();

  // ============================================
  // CREATE SESSIONS WORKSHEET
  // ============================================
  const wsSessions = XLSX.utils.json_to_sheet(sessionsData);

  // Set column widths for Sessions sheet
  const sessionsColWidths = [
    { wch: 6 },   // No.
    { wch: 12 },  // ID Sesión
    { wch: 18 },  // Fecha Inicio
    { wch: 18 },  // Fecha Final
    { wch: 12 },  // Duración
    { wch: 8 },   // Día No.
    { wch: 15 },  // Ventas Brutas
    { wch: 15 },  // Ventas Netas
    { wch: 15 },  // Total Cobrado
    { wch: 12 },  // Recibos Válidos
    { wch: 12 },  // Recibos Cancelados
    { wch: 15 },  // Ticket Promedio
    { wch: 15 },  // Total Propinas
    { wch: 20 },  // Propina Promedio por Ticket
    { wch: 15 },  // Total Descuentos
    { wch: 15 },  // Total Cupones
    { wch: 12 },  // Pagos Pendientes
    { wch: 15 },  // Total Pendiente
    { wch: 15 },  // Pérdida Bruta
    { wch: 15 },  // Pérdida Neta
    { wch: 12 },  // Usuarios Count
    { wch: 15 },  // Usuario
    { wch: 12 },  // ID Usuario
    { wch: 18 },  // Promedio Ventas Brutas
    { wch: 18 },  // Promedio Ventas Netas
    { wch: 18 },  // Promedio Pérdida Neta
    { wch: 18 }   // Promedio Total Propina
  ];

  wsSessions['!cols'] = sessionsColWidths;

  // ============================================
  // CREATE SHIFTS WORKSHEET
  // ============================================
  const wsShifts = shiftsData.length > 0 
    ? XLSX.utils.json_to_sheet(shiftsData)
    : XLSX.utils.json_to_sheet([{ 'Mensaje': 'No hay datos de turnos disponibles' }]);

  // Set column widths for Shifts sheet
  if (shiftsData.length > 0) {
    const shiftsColWidths = [
      { wch: 6 },   // No.
      { wch: 12 },  // ID Sesión
      { wch: 10 },  // ID Turno
      { wch: 20 },  // Nombre Turno
      { wch: 10 },  // Estado
      { wch: 18 },  // Fecha Inicio
      { wch: 18 },  // Fecha Final
      { wch: 12 },  // Duración
      { wch: 18 },  // Usuario Apertura
      { wch: 12 },  // ID Usuario Apertura
      { wch: 18 },  // Usuario Cierre
      { wch: 12 },  // ID Usuario Cierre
      { wch: 15 },  // Ventas Brutas
      { wch: 15 },  // Ventas Netas
      { wch: 15 },  // Total Cobrado
      { wch: 12 },  // Recibos Válidos
      { wch: 12 },  // Recibos Cancelados
      { wch: 15 },  // Ticket Promedio
      { wch: 15 },  // Total Propinas
      { wch: 20 },  // Propina Promedio por Ticket
      { wch: 20 },  // Propina Promedio por Usuario
      { wch: 15 },  // Total Descuentos
      { wch: 15 },  // Total Cupones
      { wch: 12 },  // Pagos Pendientes
      { wch: 15 },  // Total Pendiente
      { wch: 15 },  // Pérdida Bruta
      { wch: 15 },  // Pérdida Neta
      { wch: 12 }   // Usuarios Count
    ];

    wsShifts['!cols'] = shiftsColWidths;
  }

  // Add worksheets to workbook
  XLSX.utils.book_append_sheet(wb, wsSessions, 'Sesiones');
  XLSX.utils.book_append_sheet(wb, wsShifts, 'Turnos');

  const attributionRows = sortedData.flatMap(session => {
    const sessionId = session.session_id ?? session.local_session_id;
    const rows = session.opener_sales ? attributionExportRows(session.opener_sales, sessionId) : [];
    for (const shift of Object.values(session.shifts ?? {})) {
      if (shift.opener_sales) rows.push(...attributionExportRows(
        shift.opener_sales, sessionId, shift.shift_id));
    }
    return rows;
  });
  if (attributionRows.length) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(attributionRows), 'Apertura original');
  }

  // Generate filename with current date if not provided
  const defaultFileName = `Reporte_Ventas_${new Date().toISOString().split('T')[0]}.xlsx`;
  const finalFileName = fileName || defaultFileName;

  // Write and download the file
  XLSX.writeFile(wb, finalFileName);
};

/**
 * Export filtered sales data based on date range
 * Creates two sheets: Sessions (sorted DESC) and Shifts (normalized by session_id)
 */
export const exportFilteredSalesData = (
  allData: SalesStatistics[], 
  startDate: string, 
  endDate: string,
  fileName?: string
) => {
  // Filter data by date range
  const filteredData = allData.filter(session => {
    const sessionDate = new Date(session.start_timestamp).toISOString().split('T')[0];
    return sessionDate >= startDate && sessionDate <= endDate;
  });

  if (filteredData.length === 0) {
    alert(`No hay datos en el rango de fechas seleccionado (${startDate} - ${endDate})`);
    return;
  }

  const dateRangeFileName = fileName || 
    `Reporte_Ventas_${startDate}_al_${endDate}.xlsx`;
  
  exportSalesDataToExcel(filteredData, dateRangeFileName);
};
